/**
 * FikoMeasurementDisplay - Kumpas ve Cetvel Entegrasyonu
 *
 * Kumpas ve cetvel ölçüm sonuçlarını gelişmiş dimension sistemiyle görselleştirir.
 * FikoOlcumCizgisi'nin yerine veya yanında kullanılabilir.
 *
 * Özellikler:
 * - Otomatik ölçüm tipi algılama (mesafe, çap, yarıçap, açı, alan, koordinat)
 * - CATIA/SolidWorks tarzı profesyonel görünüm
 * - Tolerans gösterimi
 * - GD&T sembol desteği
 * - İnteraktif sürükle-bırak
 * - Animasyonlu geçişler
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { FikoInteractiveDimension, DimensionPreset, DimensionSegment, ArrowType, LabelPosition } from './FikoInteractiveDimension';
import { FikoCatiaDimension, CatiaDimensionType, ToleranceType, CatiaArrowStyle } from './FikoCatiaDimension';
import { FikoGDTSymbol, FikoGDTManager, GDTSymbolType, MaterialCondition } from './FikoGDTSymbol';

// ============================================================================
// TYPES & ENUMS
// ============================================================================

/**
 * Ölçüm sonuç tipi
 */
export enum MeasurementType {
    DISTANCE = 'DISTANCE',           // İki nokta arası mesafe
    DIAMETER = 'DIAMETER',           // Çap ölçümü
    RADIUS = 'RADIUS',               // Yarıçap ölçümü
    ANGLE = 'ANGLE',                 // Açı ölçümü
    AREA = 'AREA',                   // Alan ölçümü
    COORDINATE = 'COORDINATE',       // Koordinat ölçümü
    LENGTH = 'LENGTH',               // Uzunluk ölçümü
    PERPENDICULAR = 'PERPENDICULAR', // Dik mesafe
    PARALLEL = 'PARALLEL'            // Paralel mesafe
}

/**
 * Görselleştirme stili
 */
export enum DisplayStyle {
    CATIA = 'CATIA',                 // CATIA V5 yeşil stil
    SOLIDWORKS = 'SOLIDWORKS',       // SolidWorks siyah stil
    MINIMAL = 'MINIMAL',             // Minimal temiz stil
    TECHNICAL = 'TECHNICAL',         // Teknik çizim stili
    CUSTOM = 'CUSTOM'                // Özel stil
}

/**
 * Ölçüm sonucu interface
 */
export interface MeasurementResult {
    type: MeasurementType;
    value: number;
    unit: string;
    point1?: THREE.Vector3;
    point2?: THREE.Vector3;
    center?: THREE.Vector3;
    radius?: number;
    angle?: number;
    area?: number;
    normal?: THREE.Vector3;
    surfaceType?: string;
    tolerance?: {
        upper: number;
        lower: number;
    };
}

/**
 * Display konfigürasyonu
 */
export interface MeasurementDisplayConfig {
    style: DisplayStyle;
    showTolerance: boolean;
    showGDT: boolean;
    draggable: boolean;
    animated: boolean;
    precision: number;
    unit: string;
    showUnit: boolean;
    offset: number;
    arrowSize: number;
    fontSize: number;
    color?: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_DISPLAY_CONFIG: MeasurementDisplayConfig = {
    style: DisplayStyle.CATIA,
    showTolerance: false,
    showGDT: false,
    draggable: true,
    animated: true,
    precision: 3,
    unit: 'mm',
    showUnit: false,
    offset: 15,
    arrowSize: 3,
    fontSize: 12,
    color: undefined
};

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoMeasurementDisplay - Ölçüm sonuçlarını görselleştirir
 */
export class FikoMeasurementDisplay extends THREE.Group {
    readonly displayType = 'FikoMeasurementDisplay';

    // Konfigürasyon
    private config: MeasurementDisplayConfig;
    private measurement: MeasurementResult | null = null;

    // Görsel bileşenler
    private dimension: FikoInteractiveDimension | FikoCatiaDimension | null = null;
    private gdtSymbol: FikoGDTSymbol | null = null;
    private gdtManager: FikoGDTManager | null = null;

    // Durum
    private _isVisible: boolean = true;
    private _isSelected: boolean = false;

    constructor(
        measurement?: MeasurementResult,
        config: Partial<MeasurementDisplayConfig> = {}
    ) {
        super();

        this.config = { ...DEFAULT_DISPLAY_CONFIG, ...config };

        if (measurement) {
            this.setMeasurement(measurement);
        }

        this.renderOrder = 1000;
        this.frustumCulled = false;
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Ölçüm sonucunu ayarla ve görselleştir
     */
    public setMeasurement(measurement: MeasurementResult): void {
        this.measurement = measurement;
        this.rebuild();
    }

    /**
     * Ölçüm sonucunu al
     */
    public getMeasurement(): MeasurementResult | null {
        return this.measurement;
    }

    /**
     * Stili değiştir
     */
    public setStyle(style: DisplayStyle): void {
        this.config.style = style;
        this.rebuild();
    }

    /**
     * Tolerans göster/gizle
     */
    public showTolerance(show: boolean, upper?: number, lower?: number): void {
        this.config.showTolerance = show;
        if (this.measurement && upper !== undefined && lower !== undefined) {
            this.measurement.tolerance = { upper, lower };
        }
        this.rebuild();
    }

    /**
     * GD&T sembolü ekle
     */
    public addGDTSymbol(symbolType: GDTSymbolType, tolerance: number, datumRefs?: string[]): void {
        if (!this.measurement?.point1) return;

        this.config.showGDT = true;

        const attachPoint = this.measurement.point1.clone();
        const offset = new THREE.Vector3(20, 10, 0);

        this.gdtSymbol = new FikoGDTSymbol(
            {
                symbolType,
                tolerance,
                datumRefs,
                diameter: this.measurement.type === MeasurementType.DIAMETER
            },
            attachPoint,
            offset
        );

        this.add(this.gdtSymbol);
    }

    /**
     * Rengi değiştir
     */
    public setColor(color: number): void {
        this.config.color = color;
        if (this.dimension instanceof FikoInteractiveDimension) {
            this.dimension.setAllColors(color);
        }
    }

    /**
     * Offset'i değiştir
     */
    public setOffset(offset: number): void {
        this.config.offset = offset;
        if (this.dimension instanceof FikoInteractiveDimension) {
            this.dimension.setDimensionOffset(offset);
        }
    }

    /**
     * Seçili durumu
     */
    public setSelected(selected: boolean): void {
        this._isSelected = selected;
        if (this.dimension instanceof FikoInteractiveDimension) {
            if (selected) {
                this.dimension.selectSegment(DimensionSegment.LABEL);
            } else {
                this.dimension.selectSegment(null);
            }
        }
    }

    /**
     * Görünürlük
     */
    public setVisible(visible: boolean): void {
        this._isVisible = visible;
        this.visible = visible;
    }

    /**
     * Konfigürasyonu güncelle
     */
    public updateConfig(config: Partial<MeasurementDisplayConfig>): void {
        this.config = { ...this.config, ...config };
        this.rebuild();
    }

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.dispose();
        if (this.measurement) {
            this.build();
        }
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        if (this.dimension) {
            if (this.dimension instanceof FikoInteractiveDimension) {
                this.dimension.dispose();
            } else if (this.dimension instanceof FikoCatiaDimension) {
                this.dimension.dispose();
            }
            this.remove(this.dimension);
            this.dimension = null;
        }

        if (this.gdtSymbol) {
            this.gdtSymbol.dispose();
            this.remove(this.gdtSymbol);
            this.gdtSymbol = null;
        }
    }

    // ========================================================================
    // PRIVATE - BUILD
    // ========================================================================

    private build(): void {
        if (!this.measurement) return;

        switch (this.measurement.type) {
            case MeasurementType.DISTANCE:
            case MeasurementType.LENGTH:
            case MeasurementType.PERPENDICULAR:
            case MeasurementType.PARALLEL:
                this.buildDistanceDimension();
                break;
            case MeasurementType.DIAMETER:
                this.buildDiameterDimension();
                break;
            case MeasurementType.RADIUS:
                this.buildRadiusDimension();
                break;
            case MeasurementType.ANGLE:
                this.buildAngleDimension();
                break;
            case MeasurementType.AREA:
                this.buildAreaDimension();
                break;
            case MeasurementType.COORDINATE:
                this.buildCoordinateDimension();
                break;
        }
    }

    /**
     * Mesafe ölçümü görselleştir
     */
    private buildDistanceDimension(): void {
        if (!this.measurement?.point1 || !this.measurement?.point2) return;

        const start = this.measurement.point1;
        const end = this.measurement.point2;

        if (this.config.style === DisplayStyle.CATIA && this.config.showTolerance) {
            // CATIA toleranslı dimension
            this.dimension = FikoCatiaDimension.createWithTolerance(
                start,
                end,
                this.measurement.tolerance?.upper || 0,
                this.measurement.tolerance?.lower || 0,
                {
                    style: {
                        dimensionOffset: this.config.offset,
                        arrowLength: this.config.arrowSize,
                        fontSize: this.config.fontSize,
                        precision: this.config.precision,
                        showUnit: this.config.showUnit,
                        unit: this.config.unit
                    }
                }
            );
        } else {
            // Interactive dimension
            const preset = this.getPresetFromStyle();
            this.dimension = new FikoInteractiveDimension(start, end, {
                dimensionOffset: this.config.offset,
                draggable: this.config.draggable,
                label: {
                    precision: this.config.precision,
                    unit: this.config.unit,
                    showUnit: this.config.showUnit,
                    fontSize: this.config.fontSize
                } as any
            }, preset);

            if (this.config.color) {
                this.dimension.setAllColors(this.config.color);
            }
        }

        this.add(this.dimension);
    }

    /**
     * Çap ölçümü görselleştir
     */
    private buildDiameterDimension(): void {
        if (!this.measurement?.center || !this.measurement?.radius) return;

        const center = this.measurement.center;
        const radius = this.measurement.radius;

        if (this.config.style === DisplayStyle.CATIA) {
            this.dimension = FikoCatiaDimension.createDiameter(
                center,
                radius,
                0,
                {
                    style: {
                        dimensionOffset: this.config.offset,
                        arrowLength: this.config.arrowSize,
                        fontSize: this.config.fontSize,
                        precision: this.config.precision
                    },
                    tolerance: this.config.showTolerance && this.measurement.tolerance ? {
                        type: ToleranceType.ASYMMETRIC,
                        upperValue: this.measurement.tolerance.upper,
                        lowerValue: this.measurement.tolerance.lower
                    } : { type: ToleranceType.NONE }
                }
            );
        } else {
            // Çap için iki nokta oluştur
            const start = center.clone().add(new THREE.Vector3(-radius, 0, 0));
            const end = center.clone().add(new THREE.Vector3(radius, 0, 0));

            const preset = this.getPresetFromStyle();
            this.dimension = new FikoInteractiveDimension(start, end, {
                dimensionOffset: 0,
                draggable: this.config.draggable,
                label: {
                    precision: this.config.precision,
                    unit: this.config.unit,
                    showUnit: this.config.showUnit,
                    fontSize: this.config.fontSize,
                    prefix: 'Ø'
                } as any
            }, preset);

            if (this.config.color) {
                this.dimension.setAllColors(this.config.color);
            }
        }

        this.add(this.dimension);
    }

    /**
     * Yarıçap ölçümü görselleştir
     */
    private buildRadiusDimension(): void {
        if (!this.measurement?.center || !this.measurement?.radius) return;

        const center = this.measurement.center;
        const radius = this.measurement.radius;

        if (this.config.style === DisplayStyle.CATIA) {
            this.dimension = FikoCatiaDimension.createRadius(
                center,
                radius,
                Math.PI / 4, // 45 derece
                {
                    style: {
                        arrowLength: this.config.arrowSize,
                        fontSize: this.config.fontSize,
                        precision: this.config.precision
                    }
                }
            );
        } else {
            const end = center.clone().add(
                new THREE.Vector3(Math.cos(Math.PI / 4), Math.sin(Math.PI / 4), 0).multiplyScalar(radius)
            );

            const preset = this.getPresetFromStyle();
            this.dimension = new FikoInteractiveDimension(center, end, {
                dimensionOffset: 0,
                draggable: this.config.draggable,
                label: {
                    precision: this.config.precision,
                    unit: this.config.unit,
                    showUnit: this.config.showUnit,
                    fontSize: this.config.fontSize,
                    prefix: 'R'
                } as any
            }, preset);

            // Sadece bir ok göster
            this.dimension.setArrowType(ArrowType.NONE, 'start');

            if (this.config.color) {
                this.dimension.setAllColors(this.config.color);
            }
        }

        this.add(this.dimension);
    }

    /**
     * Açı ölçümü görselleştir
     */
    private buildAngleDimension(): void {
        if (!this.measurement?.point1 || !this.measurement?.point2 || !this.measurement?.center) return;

        // Açı için özel görselleştirme
        // TODO: FikoMeasurementArc kullanılabilir
        const start = this.measurement.point1;
        const end = this.measurement.point2;

        const preset = this.getPresetFromStyle();
        this.dimension = new FikoInteractiveDimension(start, end, {
            dimensionOffset: this.config.offset,
            draggable: this.config.draggable,
            label: {
                precision: 1,
                unit: '°',
                showUnit: true,
                fontSize: this.config.fontSize
            } as any
        }, preset);

        // Açı değerini manuel ayarla
        if (this.measurement.angle !== undefined) {
            // Label'ı güncelle
        }

        if (this.config.color) {
            this.dimension.setAllColors(this.config.color);
        }

        this.add(this.dimension);
    }

    /**
     * Alan ölçümü görselleştir
     */
    private buildAreaDimension(): void {
        if (!this.measurement?.center || !this.measurement?.area) return;

        // Alan için merkez noktada label
        const center = this.measurement.center;
        const dummyEnd = center.clone().add(new THREE.Vector3(0.001, 0, 0));

        const preset = this.getPresetFromStyle();
        this.dimension = new FikoInteractiveDimension(center, dummyEnd, {
            dimensionOffset: 0,
            draggable: this.config.draggable,
            extensionLine1: { visible: false } as any,
            extensionLine2: { visible: false } as any,
            arrow1: { type: ArrowType.NONE } as any,
            arrow2: { type: ArrowType.NONE } as any,
            dimensionLine: { visible: false } as any,
            label: {
                precision: this.config.precision,
                unit: this.config.unit + '²',
                showUnit: true,
                fontSize: this.config.fontSize
            } as any
        }, preset);

        if (this.config.color) {
            this.dimension.setAllColors(this.config.color);
        }

        this.add(this.dimension);
    }

    /**
     * Koordinat ölçümü görselleştir
     */
    private buildCoordinateDimension(): void {
        if (!this.measurement?.point1) return;

        const point = this.measurement.point1;

        // Koordinat için crosshair ve label
        // X, Y, Z değerlerini göster
        const crosshairSize = 5;
        const crosshairGroup = new THREE.Group();

        // X çizgisi (kırmızı)
        const xGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-crosshairSize, 0, 0),
            new THREE.Vector3(crosshairSize, 0, 0)
        ]);
        const xMat = new THREE.LineBasicMaterial({ color: 0xff0000 });
        crosshairGroup.add(new THREE.Line(xGeom, xMat));

        // Y çizgisi (yeşil)
        const yGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, -crosshairSize, 0),
            new THREE.Vector3(0, crosshairSize, 0)
        ]);
        const yMat = new THREE.LineBasicMaterial({ color: 0x00ff00 });
        crosshairGroup.add(new THREE.Line(yGeom, yMat));

        // Z çizgisi (mavi)
        const zGeom = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, -crosshairSize),
            new THREE.Vector3(0, 0, crosshairSize)
        ]);
        const zMat = new THREE.LineBasicMaterial({ color: 0x0000ff });
        crosshairGroup.add(new THREE.Line(zGeom, zMat));

        crosshairGroup.position.copy(point);
        this.add(crosshairGroup);

        // Koordinat label'ı
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 256;
        canvas.height = 64;

        ctx.fillStyle = '#000000';
        ctx.font = `bold ${this.config.fontSize * 2}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const coordText = `X: ${point.x.toFixed(this.config.precision)} Y: ${point.y.toFixed(this.config.precision)} Z: ${point.z.toFixed(this.config.precision)}`;
        ctx.fillText(coordText, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true });
        const sprite = new THREE.Sprite(spriteMat);
        sprite.position.copy(point).add(new THREE.Vector3(0, 10, 0));
        sprite.scale.set(20, 5, 1);

        this.add(sprite);
    }

    /**
     * Style'dan preset'e dönüştür
     */
    private getPresetFromStyle(): DimensionPreset {
        switch (this.config.style) {
            case DisplayStyle.CATIA:
                return DimensionPreset.CATIA;
            case DisplayStyle.SOLIDWORKS:
                return DimensionPreset.SOLIDWORKS;
            case DisplayStyle.MINIMAL:
                return DimensionPreset.MINIMAL;
            case DisplayStyle.TECHNICAL:
                return DimensionPreset.TECHNICAL;
            default:
                return DimensionPreset.SOLIDWORKS;
        }
    }
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Mesafe ölçümü görselleştir
 */
export function createDistanceDisplay(
    point1: THREE.Vector3,
    point2: THREE.Vector3,
    config: Partial<MeasurementDisplayConfig> = {}
): FikoMeasurementDisplay {
    return new FikoMeasurementDisplay({
        type: MeasurementType.DISTANCE,
        value: point1.distanceTo(point2),
        unit: config.unit || 'mm',
        point1,
        point2
    }, config);
}

/**
 * Çap ölçümü görselleştir
 */
export function createDiameterDisplay(
    center: THREE.Vector3,
    radius: number,
    config: Partial<MeasurementDisplayConfig> = {}
): FikoMeasurementDisplay {
    return new FikoMeasurementDisplay({
        type: MeasurementType.DIAMETER,
        value: radius * 2,
        unit: config.unit || 'mm',
        center,
        radius
    }, config);
}

/**
 * Yarıçap ölçümü görselleştir
 */
export function createRadiusDisplay(
    center: THREE.Vector3,
    radius: number,
    config: Partial<MeasurementDisplayConfig> = {}
): FikoMeasurementDisplay {
    return new FikoMeasurementDisplay({
        type: MeasurementType.RADIUS,
        value: radius,
        unit: config.unit || 'mm',
        center,
        radius
    }, config);
}

/**
 * Açı ölçümü görselleştir
 */
export function createAngleDisplay(
    center: THREE.Vector3,
    point1: THREE.Vector3,
    point2: THREE.Vector3,
    angle: number,
    config: Partial<MeasurementDisplayConfig> = {}
): FikoMeasurementDisplay {
    return new FikoMeasurementDisplay({
        type: MeasurementType.ANGLE,
        value: angle,
        unit: '°',
        point1,
        point2,
        center,
        angle
    }, config);
}

/**
 * Koordinat görselleştir
 */
export function createCoordinateDisplay(
    point: THREE.Vector3,
    config: Partial<MeasurementDisplayConfig> = {}
): FikoMeasurementDisplay {
    return new FikoMeasurementDisplay({
        type: MeasurementType.COORDINATE,
        value: 0,
        unit: config.unit || 'mm',
        point1: point
    }, config);
}

/**
 * Alan ölçümü görselleştir
 */
export function createAreaDisplay(
    center: THREE.Vector3,
    area: number,
    config: Partial<MeasurementDisplayConfig> = {}
): FikoMeasurementDisplay {
    return new FikoMeasurementDisplay({
        type: MeasurementType.AREA,
        value: area,
        unit: config.unit || 'mm',
        center,
        area
    }, config);
}

